import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { Toaster } from 'react-hot-toast';
import './i18n';
import './styles.css';
import { AuthProvider, useAuth } from './lib/auth';
import { createAppRouter } from './router';

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: 1, staleTime: 15_000, refetchOnWindowFocus: false } } });

function App() {
  const auth = useAuth();
  const router = React.useMemo(() => createAppRouter(), []);
  // The route guards read the signed-in user from the router context. When that changes (sign-in, sign-out, a
  // forced password change completed, a session that expired) the guards must run again with the NEW context.
  // Pages used to navigate right after `await login()`, racing React's re-render: the guard still saw the old
  // state and bounced the user back to the sign-in page or to the change-password form (found 2026-09-20).
  const identity = `${auth.me?.id ?? ''}:${auth.me?.must_change_password ?? ''}`;
  React.useEffect(() => {
    if (auth.ready) void router.invalidate();
  }, [router, auth.ready, identity]);
  if (!auth.ready) return <div className="flex min-h-screen items-center justify-center text-sm text-neutral-500">…</div>;
  return <RouterProvider router={router} context={{ auth }} />;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <App />
        <Toaster position="bottom-center" />
      </AuthProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
